import { AlertTriangle, CreditCard, ShieldCheck } from "lucide-react";
import { useEffect, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { replaceLocation } from "../browser-navigation";
import { Brand } from "../components/Brand";

export type PaymentBrowser = "alipay" | "wechat" | "browser";

export function detectPaymentBrowser(userAgent: string): PaymentBrowser {
  if (/MicroMessenger/i.test(userAgent)) return "wechat";
  if (/AlipayClient/i.test(userAgent)) return "alipay";
  return "browser";
}

export function AlipayBridgePage() {
  const [searchParams] = useSearchParams();
  const browser = detectPaymentBrowser(navigator.userAgent);
  const orderId = searchParams.get("orderId");
  const expires = searchParams.get("expires");
  const signature = searchParams.get("signature");
  const paymentParams =
    orderId && expires && signature ? { orderId, expires, signature } : null;
  const checkoutPath = paymentParams
    ? "/api/payments/fubei/checkout?" + new URLSearchParams(paymentParams)
    : null;
  const autoRedirected = useRef(false);

  useEffect(() => {
    if (browser !== "alipay" || !checkoutPath || autoRedirected.current) return;
    autoRedirected.current = true;
    replaceLocation(checkoutPath);
  }, [browser, checkoutPath]);

  return (
    <main className="bridge-page">
      <Brand />
      <section className="bridge-card">
        {!paymentParams ? (
          <>
            <span className="bridge-symbol warning"><AlertTriangle size={31} /></span>
            <p className="eyebrow">Payment link</p>
            <h1>付款链接无效</h1>
            <p>链接参数不完整，请回到 GAMEFRAME 权益页重新生成付款码。</p>
            <Link className="button button-primary button-full" to="/pricing">
              返回权益页
            </Link>
          </>
        ) : browser === "wechat" ? (
          <>
            <span className="bridge-symbol warning"><AlertTriangle size={31} /></span>
            <p className="eyebrow">WeChat browser</p>
            <h1>请使用支付宝 App 扫码</h1>
            <p>
              微信内无法安全拉起本次支付宝付款。请返回显示二维码的页面，
              打开支付宝 App 的“扫一扫”重新扫描。
            </p>
            <div className="bridge-tip">
              <ShieldCheck size={18} />
              <span>请勿在聊天中转发付款链接；权益只会发放到原下单账户。</span>
            </div>
          </>
        ) : (
          <>
            <span className="bridge-symbol alipay"><CreditCard size={31} /></span>
            <p className="eyebrow">
              {browser === "alipay" ? "Alipay detected" : "Mobile website payment"}
            </p>
            <h1>使用支付宝完成付款</h1>
            <p>
              本次为一次性付款，不会开通自动续费。请核对支付宝收银台显示的商品和金额。
            </p>
            <a
              className="button button-primary button-full"
              href={checkoutPath!}
              referrerPolicy="no-referrer"
            >
              <CreditCard size={17} />打开支付宝付款
            </a>
            <p className="bridge-footnote">
              {browser === "alipay"
                ? "正在自动打开支付宝收银台；若未成功跳转，请点击上方按钮继续。"
                : "点击后会尝试唤起支付宝 App；若未自动打开，请按支付宝页面提示继续。"}
            </p>
          </>
        )}
      </section>
    </main>
  );
}
